import { api } from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BookingCity {
  id: number;
  name: string;
  branch_code: string;
  address: string | null;
}

export interface BookingFlat {
  id: number;
  city_id: number;
  name: string;
  address: string | null;
  model_url: string | null;
  caretaker_name: string | null;
  caretaker_email: string | null;
}

export interface BookingRoom {
  id: number;
  flat_id: number;
  room_name: string;
  room_type: 'single' | 'twin';
  bed_count: number;
  room_size: string | null;
  floor: string | null;
  beds: BookingBed[];
}

export interface BookingBed {
  id: number;
  room_id: number;
  bed_label: string;
  bed_type: 'single' | 'twin';
  is_bookable: boolean;
}

export interface FlatBooking {
  id: number;
  bed_id: number;
  user_id: number;
  check_in_date: string;
  check_out_date: string;
  arrival_time: string | null;
  status: string;
  approved_by: number | null;
  cancelled_by: string | null;
  cancelled_by_user_id: number | null;
  cancelled_by_user_name: string | null;
  cancellation_reason: string | null;
  booking_group_id: string | null;
  created_at: string;
  updated_at: string;
  bed_label: string;
  bed_type: string;
  room_name: string;
  room_type: string;
  flat_name: string;
  city_name: string;
  user_name: string;
  user_email: string;
  employee_id: string | null;
  approved_by_name: string | null;
}

export interface BedCalendarEntry {
  check_in_date: string;
  check_out_date: string;
  status: string;
}

export interface BookingDashboardStats {
  total_pending: number;
  /** Approved and still to start — a stay happening now is counted in total_ongoing. */
  total_approved: number;
  total_ongoing: number;
  total_completed: number;
  total_denied: number;
  total_cancelled: number;
  total_terminated: number;
}

// ─── Inventory ────────────────────────────────────────────────────────────────

export function getCities(): Promise<{ cities: BookingCity[] }> {
  return api.get('/api/booking/cities');
}

export function getFlatsByCity(cityId: number): Promise<{ flats: BookingFlat[] }> {
  return api.get(`/api/booking/cities/${cityId}/flats`);
}

export function getFlatModel(flatId: number): Promise<{ flat: BookingFlat & { rooms: BookingRoom[] } }> {
  return api.get(`/api/booking/flats/${flatId}/model`);
}

export function getBedsByRoom(roomId: number): Promise<{ beds: BookingBed[] }> {
  return api.get(`/api/booking/rooms/${roomId}/beds`);
}

export function getBedCalendar(bedId: number, from: string, to: string): Promise<{ calendar: BedCalendarEntry[] }> {
  return api.get(`/api/booking/beds/${bedId}/calendar?from=${from}&to=${to}`);
}

export function getLockMode(): Promise<{ mode: string }> {
  return api.get('/api/booking/settings/lock-mode');
}

// ─── Employee booking operations ──────────────────────────────────────────────

export function createFlatBooking(data: {
  bed_id: number;
  check_in_date: string;
  check_out_date: string;
  arrival_time?: string | null;
}): Promise<{ booking: FlatBooking }> {
  return api.post('/api/booking/flat-bookings', data);
}

export function getMyFlatBookings(): Promise<{ bookings: FlatBooking[] }> {
  return api.get('/api/booking/flat-bookings/me');
}

export interface CalendarBooking {
  id: number;
  bed_id: number;
  check_in_date: string;
  check_out_date: string;
  status: string;
  bed_label: string;
  bed_type: string;
  room_name: string;
  room_type: string;
  flat_name: string;
  city_name: string;
}

export function getCalendarFlatBookings(dateFrom: string, dateTo: string): Promise<{ bookings: CalendarBooking[] }> {
  return api.get(`/api/booking/flat-bookings/calendar?date_from=${dateFrom}&date_to=${dateTo}`);
}

export function getFlatBooking(id: number): Promise<{ booking: FlatBooking }> {
  return api.get(`/api/booking/flat-bookings/${id}`);
}

export function cancelMyFlatBooking(id: number, reason?: string): Promise<{ booking: FlatBooking }> {
  return api.put(`/api/booking/flat-bookings/${id}/cancel`, { reason });
}

export function getMyBookingDashboard(): Promise<{
  pending: FlatBooking[];
  upcoming: FlatBooking[];
  past: FlatBooking[];
  all: FlatBooking[];
}> {
  return api.get('/api/booking/dashboard/me');
}

// ─── Smart booking ───────────────────────────────────────────────────────────

export interface SmartBookSegment {
  bed_id: number;
  bed_label: string;
  bed_type: string;
  room_name: string;
  room_type: string;
  room_id: number;
  flat_name: string;
  check_in_date: string;
  check_out_date: string;
}

export interface DirectBed {
  bed_id: number;
  bed_label: string;
  bed_type: string;
  room_name: string;
  room_type: string;
  room_id: number;
  flat_name: string;
}

export function smartBookSuggest(data: {
  flat_id: number;
  check_in_date: string;
  check_out_date: string;
  bed_type?: 'single' | 'twin';
}): Promise<{
  possible: boolean;
  segments: SmartBookSegment[];
  direct_beds: DirectBed[];
  total_beds?: number;
  lock_mode?: string;
  uncovered_from?: string;
  message?: string;
}> {
  return api.post('/api/booking/smart-book/suggest', data);
}

export function smartBookCreate(data: {
  segments: Array<{ bed_id: number; check_in_date: string; check_out_date: string; arrival_time?: string | null }>;
}): Promise<{ bookings: FlatBooking[] }> {
  return api.post('/api/booking/smart-book', data);
}

export function cancelBookingGroup(groupId: string, reason?: string): Promise<{ cancelled: number; bookings: FlatBooking[] }> {
  return api.put(`/api/booking/booking-groups/${groupId}/cancel`, { reason });
}

// ─── Admin operations ─────────────────────────────────────────────────────────

/**
 * A bed-cycling (smart) booking is one request split across several beds, so it is
 * approved or denied as a whole rather than segment by segment.
 */
export function approveBookingGroup(groupId: string): Promise<{ bookings: FlatBooking[]; approved: number }> {
  return api.put(`/api/admin/booking/booking-groups/${groupId}/approve`);
}

export function denyBookingGroup(groupId: string, reason?: string): Promise<{ bookings: FlatBooking[]; denied: number }> {
  return api.put(`/api/admin/booking/booking-groups/${groupId}/deny`, { reason });
}


export interface OfficeRoomBookingStats {
  total_pending: number;
  /** Approved and still to start — a meeting running now is counted in total_ongoing. */
  total_approved: number;
  total_ongoing: number;
  total_completed: number;
  total_denied: number;
  total_cancelled: number;
}

export function getAdminBookingDashboard(): Promise<{
  stats: BookingDashboardStats & { flat: BookingDashboardStats; room: OfficeRoomBookingStats };
  pending_requests: FlatBooking[];
  pending_room_requests: OfficeRoomBooking[];
}> {
  return api.get('/api/admin/booking/dashboard');
}

export function getAllFlatBookings(filters?: {
  status?: string;
  city_id?: number;
  user_id?: number;
  date_from?: string;
  date_to?: string;
  /** Narrows 'approved' to bookings still ahead, happening now, or already finished. */
  timeframe?: 'upcoming' | 'past' | 'ongoing';
  limit?: number;
  offset?: number;
}): Promise<{ bookings: FlatBooking[]; total: number }> {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== '') params.set(k, String(v));
    });
  }
  const qs = params.toString();
  return api.get(`/api/admin/booking/flat-bookings${qs ? `?${qs}` : ''}`);
}

export function approveFlatBooking(id: number): Promise<{ booking: FlatBooking }> {
  return api.put(`/api/admin/booking/flat-bookings/${id}/approve`);
}

export function denyFlatBooking(id: number, reason?: string): Promise<{ booking: FlatBooking }> {
  return api.put(`/api/admin/booking/flat-bookings/${id}/deny`, { reason });
}

export function adminCancelFlatBooking(id: number, reason?: string): Promise<{ booking: FlatBooking }> {
  return api.put(`/api/admin/booking/flat-bookings/${id}/cancel`, { reason });
}

export function terminateFlatBooking(id: number): Promise<{ booking: FlatBooking }> {
  return api.put(`/api/admin/booking/flat-bookings/${id}/terminate`);
}

export function setLockMode(mode: 'soft' | 'hard'): Promise<{ mode: string }> {
  return api.put('/api/admin/booking/settings/lock-mode', { mode });
}

// ─── Admin office room operations ───────────────────────────────────────────

export function getAllOfficeRoomBookings(filters?: {
  status?: string;
  city_id?: number;
  user_id?: number;
  date_from?: string;
  date_to?: string;
  /** Narrows 'approved' to bookings still ahead, happening now, or already finished. */
  timeframe?: 'upcoming' | 'past' | 'ongoing';
  limit?: number;
  offset?: number;
}): Promise<{ bookings: OfficeRoomBooking[]; total: number }> {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== '') params.set(k, String(v));
    });
  }
  const qs = params.toString();
  return api.get(`/api/admin/booking/office-room-bookings${qs ? `?${qs}` : ''}`);
}

export function approveOfficeRoomBooking(id: number): Promise<{ booking: OfficeRoomBooking }> {
  return api.put(`/api/admin/booking/office-room-bookings/${id}/approve`);
}

export function denyOfficeRoomBooking(id: number, reason?: string): Promise<{ booking: OfficeRoomBooking }> {
  return api.put(`/api/admin/booking/office-room-bookings/${id}/deny`, { reason });
}

export function adminCancelOfficeRoomBooking(id: number, reason?: string): Promise<{ booking: OfficeRoomBooking }> {
  return api.put(`/api/admin/booking/office-room-bookings/${id}/cancel`, { reason });
}

// ─── Office rooms ───────────────────────────────────────────────────────────

export interface OfficeRoom {
  id: number;
  city_id: number;
  room_name: string;
  room_type: 'conference' | 'meeting';
  capacity: number;
  amenities: string[];
  model_id: string | null;
  is_bookable: boolean;
}

export interface OfficeRoomBooking {
  id: number;
  room_id: number;
  user_id: number;
  booking_date: string;
  start_time: string;
  end_time: string;
  purpose: string | null;
  status: string;
  cancelled_by: string | null;
  cancelled_by_user_id: number | null;
  cancelled_by_user_name: string | null;
  cancellation_reason: string | null;
  created_at: string;
  updated_at: string;
  room_name: string;
  room_type: string;
  capacity: number;
  city_name: string;
  city_id: number;
  user_name: string;
  user_email: string;
  employee_id: string | null;
  invitees?: RoomBookingInvitee[];
}

export interface RoomBookingInvitee {
  user_id: number;
  name: string;
  email: string;
}

export interface DirectoryUser {
  id: number;
  name: string;
  email: string;
  designation: string | null;
}

/** Active-user directory for the meeting invite picker (any authed user). */
export function getBookingDirectory(): Promise<{ users: DirectoryUser[] }> {
  return api.get('/api/booking/office-rooms/directory');
}

export function getOfficeRooms(filters?: {
  city_id?: number;
  type?: 'conference' | 'meeting';
}): Promise<{ rooms: OfficeRoom[] }> {
  const params = new URLSearchParams();
  if (filters?.city_id) params.set('city_id', String(filters.city_id));
  if (filters?.type) params.set('type', filters.type);
  const qs = params.toString();
  return api.get(`/api/booking/office-rooms${qs ? `?${qs}` : ''}`);
}

export function getOfficeRoom(id: number): Promise<{ room: OfficeRoom }> {
  return api.get(`/api/booking/office-rooms/${id}`);
}

export function getOfficeRoomCalendar(roomId: number, date: string): Promise<{ bookings: OfficeRoomBooking[] }> {
  return api.get(`/api/booking/office-rooms/${roomId}/calendar?date=${date}`);
}

export function createOfficeRoomBooking(data: {
  room_id: number;
  booking_date: string;
  start_time: string;
  end_time: string;
  purpose?: string;
  invitee_ids?: number[];
  /** A whole-day hold may start earlier than now; every other slot may not. */
  all_day?: boolean;
}): Promise<{ booking: OfficeRoomBooking }> {
  return api.post('/api/booking/office-rooms/bookings', data);
}

export function getMyOfficeRoomBookings(): Promise<{ bookings: OfficeRoomBooking[] }> {
  return api.get('/api/booking/office-rooms/bookings/me');
}

/** Meeting-room bookings the current user was invited to (view-only). */
export function getSharedOfficeRoomBookings(): Promise<{ bookings: OfficeRoomBooking[] }> {
  return api.get('/api/booking/office-rooms/bookings/shared');
}

export function addOfficeRoomInvitees(bookingId: number, userIds: number[]): Promise<{ booking: OfficeRoomBooking }> {
  return api.post(`/api/booking/office-rooms/bookings/${bookingId}/invitees`, { user_ids: userIds });
}

export function removeOfficeRoomInvitee(bookingId: number, userId: number): Promise<{ booking: OfficeRoomBooking }> {
  return api.delete(`/api/booking/office-rooms/bookings/${bookingId}/invitees/${userId}`);
}

export function cancelOfficeRoomBooking(id: number, reason?: string): Promise<{ booking: OfficeRoomBooking }> {
  return api.put(`/api/booking/office-rooms/bookings/${id}/cancel`, { reason });
}

// ─── SOS ────────────────────────────────────────────────────────────────────

export type SosCategory = 'medical' | 'security' | 'fire_gas' | 'maintenance' | 'other';
export type SosStatus = 'open' | 'acknowledged' | 'resolved' | 'cancelled_by_user';

export interface SosAlert {
  id: number;
  user_id: number;
  booking_id: number | null;
  flat_id: number | null;
  city_id: number | null;
  location_label: string | null;
  location_address: string | null;
  location_confirmed: boolean;
  category: SosCategory;
  message: string | null;
  contact_phone: string | null;
  status: SosStatus;
  acknowledged_by: number | null;
  acknowledged_at: string | null;
  resolved_by: number | null;
  resolved_at: string | null;
  resolution_note: string | null;
  created_at: string;
  user_name: string;
  user_email: string;
  employee_id: string | null;
  acknowledged_by_name: string | null;
  resolved_by_name: string | null;
}

export interface SosRecipient {
  id: number;
  user_id: number;
  is_active: boolean;
  user_name: string;
  user_email: string;
  user_phone: string | null;
  added_at: string;
}

// Employee
export function raiseSos(data: { category: SosCategory; message?: string }): Promise<{ alert: SosAlert }> {
  return api.post('/api/booking/sos', data);
}

export function getActiveSos(): Promise<{ alert: SosAlert | null }> {
  return api.get('/api/booking/sos/active');
}

export function getSosEligibility(): Promise<{ eligible: boolean; location: string | null; alert: SosAlert | null }> {
  return api.get('/api/booking/sos/eligibility');
}

export function getMySos(): Promise<{ alerts: SosAlert[] }> {
  return api.get('/api/booking/sos/me');
}

export function cancelSos(id: number): Promise<{ alert: SosAlert }> {
  return api.put(`/api/booking/sos/${id}/cancel`);
}

// Responder (admin)
export function getSosInbox(history = false): Promise<{ alerts: SosAlert[] }> {
  return api.get(`/api/admin/booking/sos${history ? '?history=1' : ''}`);
}

export function ackSos(id: number): Promise<{ alert: SosAlert }> {
  return api.put(`/api/admin/booking/sos/${id}/ack`);
}

export function resolveSos(id: number, note?: string): Promise<{ alert: SosAlert }> {
  return api.put(`/api/admin/booking/sos/${id}/resolve`, { note });
}

export function getSosRecipients(): Promise<{ recipients: SosRecipient[] }> {
  return api.get('/api/admin/booking/sos/recipients');
}

export function addSosRecipient(userId: number): Promise<{ recipients: SosRecipient[] }> {
  return api.post('/api/admin/booking/sos/recipients', { user_id: userId });
}

export function removeSosRecipient(userId: number): Promise<{ recipients: SosRecipient[] }> {
  return api.delete(`/api/admin/booking/sos/recipients/${userId}`);
}

// Booking approvers (Settings)
export interface BookingApprover {
  id: number;
  user_id: number;
  is_active: boolean;
  user_name: string;
  user_email: string;
  added_at: string;
}

export interface ApproverCandidate { id: number; name: string; microsoft_email: string }

export function getBookingApprovers(): Promise<{ approvers: BookingApprover[] }> {
  return api.get('/api/admin/booking/approvers');
}

/** Admins eligible to be designated as booking approvers. */
export function getApproverCandidates(): Promise<{ candidates: ApproverCandidate[] }> {
  return api.get('/api/admin/booking/approver-candidates');
}

export function addBookingApprover(userId: number): Promise<{ approvers: BookingApprover[] }> {
  return api.post('/api/admin/booking/approvers', { user_id: userId });
}

export function removeBookingApprover(userId: number): Promise<{ approvers: BookingApprover[] }> {
  return api.delete(`/api/admin/booking/approvers/${userId}`);
}
